
  import { defineComponent, ref, computed } from 'vue';
  import { Box } from '@/ui/components/Box';
  import { Button } from '@/ui/components/Button';
  import { InputField } from '@/ui/components/form/InputField';
  import { ApprovalStatus, ApprovalType } from '@/entities/approvals/constants';
  import { toasted } from '@/ui/helpers';
  import { t } from '@/ui/i18n/i18n';
  import { update as updateApproval } from '@/services/api/resources/approval';
  import { ApprovalForm } from '@/entities/approvals/model';
  import { useValidationErrors } from '@/ui/concerns/entityItems/useValidationErrors';
  import { useTask } from '@/ui/concerns';
  import { v4 as uuidv4 } from 'uuid';

  export interface ApproveFormProps {
    inspectionId: number;
    approvalId: number;
    issueId: number;
  }

  export default defineComponent<ApproveFormProps>({
    name: 'ApproveForm',
    components: {
      InputField,
      Box,
      Button,
    },
    props: ['inspectionId', 'approvalId', 'issueId'],
    emits: ['updated'],
    setup(props, context) {
      const comment = ref('');
      const { displayValidationErrors } = useValidationErrors();

      const title = computed(() => {
        if (props.issueId) {
          return t('approve.issue.title');
        }
        if (props.inspectionId) {
          return t('approve.form.title');
        }
        return '';
      });

      const description = computed(() => {
        if (props.issueId) {
          return t('approve.issue.description');
        }
        if (props.inspectionId) {
          return t('approve.form.description');
        }
        return '';
      });

      const updateTask = (data: ApprovalForm, id: number, status: ApprovalStatus) => useTask(
        () => updateApproval(data, id),
        () => {
          toasted.success(t('approve.form.success'));

          context.emit('updated', status);
        },
        uuidv4(),
        error => {
          displayValidationErrors(error);
          console.error(error);
        },
      );

      const update = async (status: ApprovalStatus) => {
        const data: ApprovalForm = {
          inspection: props.inspectionId,
          issue: props.issueId,
          type: props.inspectionId ? ApprovalType.Inspection : ApprovalType.Issue,
          status,
          comment: comment.value,
        };

        await updateTask(data, props.approvalId, status).run();
      };

      return {
        ApprovalStatus,
        comment,
        update,
        title,
        description,
      };
    },
  });
